"use client"

import { Blog } from "@types"
import { Button } from "@/components/ui/button"
import { setActiveTeam } from "@/lib/actions/setActiveTeam"
import Avatar from "@/app/components/Avatar"
import { Check, ChevronDown, Loader2 } from "lucide-react"
import { ReactNode, useState } from "react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { BlogLogoImage } from "@/app/(app)/(blog)/components/BlogLogo"
import { usePathname } from "next/navigation"

function DropdownWrapper({ children }: { children: ReactNode }) {
  return (
    <DropdownMenuTrigger asChild className="font-normal">
      <Button variant="outline" className="font-normal flex justify-center">
        {children}
      </Button>
    </DropdownMenuTrigger>
  )
}

/**
 * If a user switches teams and is on one of these routes, we push
 * them to the dashboard, since reloading would break the page.
 *
 * Note that this is a partial match!
 */
const REDIRECT_ROUTES = ["/editor", "/dashboard/metrics"]

/**
 * The routes that we want to display the team switcher on.
 *
 * Note, it's a partial match.
 */
const DISPLAY_ROUTES = [
  "/dashboard",

  // All settings pages
  "/settings/publication",
  "/subscribers",
]

export default function TeamSwitcherToggle({
  blogs,
  impersonatingBlog,
  usersBlog,
}: {
  /**
   * The ID of the blog that the user is impersonating.
   *
   * If this is undefined, the user is working on their own blog.
   */
  impersonatingBlog?: Blog
  /**
   * The user's actual blog they own.
   */
  usersBlog: Blog
  /**
   * A set of all blogs (INCLUDING the user's blog they own)
   * that the user is a team member of.
   */
  blogs: Array<Blog>
}) {
  const [loading, setLoading] = useState(false)
  const pathname = usePathname()

  if (!DISPLAY_ROUTES.some((route) => pathname?.startsWith(route))) {
    return null
  }

  const handleChangeTeam = async (blogId: string) => {
    if (loading) return
    setLoading(true)
    await setActiveTeam(blogId)

    if (
      REDIRECT_ROUTES.some((route) => window.location.pathname.includes(route))
    ) {
      window.location.replace("/dashboard")
    } else {
      window.location.reload()
    }
  }

  const blog = impersonatingBlog || usersBlog

  if (loading)
    return (
      <DropdownMenu>
        <DropdownWrapper>
          <div className="flex justify-center w-full ">
            {/* Fake name, so we have the proper width */}
            <span className="opacity-0">{blog?.name || "Personal"}</span>
            <Loader2 className="absolute animate-spin h-5 w-5 mx-auto" />
          </div>
        </DropdownWrapper>
      </DropdownMenu>
    )

  return (
    <DropdownMenu>
      <DropdownWrapper>
        <BlogLogoImage
          src={blog?.logo_url || ""}
          alt={blog?.name}
          size={20}
          className="h-5 w-5 mr-1"
        />
        <span className="hidden sm:block ml-1 ">
          {blog?.name || "Personal"}
        </span>
        <ChevronDown className="h-4 w-4 ml-2" />
      </DropdownWrapper>
      <DropdownMenuContent className="">
        <DropdownMenuLabel className="font-normal text-foreground">
          Choose a publication
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {blogs.map((blog) => (
          <DropdownMenuItem key={blog.id} className="p-0">
            <ChangeTeamBtn
              blog={blog}
              isActive={
                blog.id === impersonatingBlog?.id ||
                (!impersonatingBlog && blog.id === usersBlog.id)
              }
              handleChangeTeam={handleChangeTeam}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ChangeTeamBtn({
  blog,
  handleChangeTeam,
  isActive,
}: {
  blog: Blog
  isActive: boolean
  handleChangeTeam: (blogId: string) => void
}) {
  return (
    <Button
      variant="ghost"
      className="w-full justify-start font-normal"
      onClick={() => handleChangeTeam(blog.id)}
    >
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-row ">
          <Avatar
            src={blog.logo_url}
            alt={blog.name}
            size={20}
            className="h-5 w-5 mr-2 rounded-md"
            imageClassName="rounded-md"
            textFallback={blog.name.charAt(0)}
          />
          {blog.name}
        </div>
        {isActive && <Check className="h-4 w-4 ml-4 " />}
      </div>
    </Button>
  )
}
